import axios from 'axios'
import { catalogPreview } from '@/config/localPreview'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || (window.location.hostname.includes('.com') ? 'https://api.banjinos.com/api' : 'https://api.banjinos.dev/api'),
  headers: {
    'Content-Type': 'application/json'
  }
})

apiClient.interceptors.request.use(config => {
  if (catalogPreview && config.url?.startsWith('/v1/shop/') && !['get', 'head'].includes(config.method)) {
    return Promise.reject(new Error('Checkout is disabled in the local catalog preview.'))
  }
  return config
})

// This dev-server route forwards only public catalog GETs, never cart/order traffic.
const catalogClient = catalogPreview ? axios.create({ baseURL: '/__local-catalog' }) : apiClient

apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error)
    return Promise.reject(error)
  }
)

// Export apiClient for direct access when needed
export { apiClient }

export default {
  // Vendors
  vendors: {
    getAll: () => apiClient.get('/vendors'),
    getById: (id) => apiClient.get(`/vendors/${id}`),
    create: (data) => apiClient.post('/vendors', data),
    update: (id, data) => apiClient.put(`/vendors/${id}`, data),
    delete: (id) => apiClient.delete(`/vendors/${id}`),
    safeDelete: (id) => apiClient.delete(`/vendors-safe/${id}/safe`),
    forceDelete: (id) => apiClient.delete(`/vendors-safe/${id}/force`)
  },
  
  // Materials (formerly Ingredients)
  materials: {
    getAll: () => apiClient.get('/materials?limit=100'),
    getById: (id) => apiClient.get(`/materials/${id}`),
    create: (data) => apiClient.post('/materials', data),
    update: (id, data) => apiClient.put(`/materials/${id}`, data),
    delete: (id) => apiClient.delete(`/materials/${id}`),
    
    // Pricing Tiers
    createPricingTier: (materialId, data) => 
      apiClient.post(`/materials/${materialId}/pricing-tiers`, data),
    updatePricingTier: (materialId, tierId, data) => 
      apiClient.put(`/materials/${materialId}/pricing-tiers/${tierId}`, data),
    deletePricingTier: (materialId, tierId) => 
      apiClient.delete(`/materials/${materialId}/pricing-tiers/${tierId}`)
  },
  
  // Recipes
  recipes: {
    getAll: () => apiClient.get('/recipes'),
    getById: (id) => apiClient.get(`/recipes/${id}`),
    create: (data) => apiClient.post('/recipes', data),
    update: (id, data) => apiClient.put(`/recipes/${id}`, data),
    delete: (id) => apiClient.delete(`/recipes/${id}`),
    getScenarios: (id) => apiClient.get(`/recipes/${id}/scenarios`)
  },
  
  // Production Scenarios
  scenarios: {
    getAll: () => apiClient.get('/scenarios'),
    getById: (id) => apiClient.get(`/scenarios/${id}`),
    create: (data) => apiClient.post('/scenarios', data),
    update: (id, data) => apiClient.put(`/scenarios/${id}`, data),
    delete: (id) => apiClient.delete(`/scenarios/${id}`),
    getCosts: (id) => apiClient.get(`/scenarios/${id}/costs`)
  },
  
  // Cost Calculations
  costs: {
    calculate: (scenarioId) => apiClient.post(`/costs/calculate/${scenarioId}`),
    save: (scenarioId) => apiClient.post(`/costs/save/${scenarioId}`),
    compareByRecipe: (recipeId) => apiClient.get(`/costs/compare/recipe/${recipeId}`)
  },
  
  // Batches
  batches: {
    getAll: () => apiClient.get('/batches'),
    getById: (id) => apiClient.get(`/batches/${id}`),
    create: (data) => apiClient.post('/batches', data),
    update: (id, data) => apiClient.put(`/batches/${id}`, data),
    delete: (id) => apiClient.delete(`/batches/${id}`),
    calculateCost: (data) => apiClient.post('/batches/calculate-cost', data)
  },
  
  // Ingredients (alias for materials for backward compatibility)
  ingredients: {
    getAll: () => apiClient.get('/materials'),
    getById: (id) => apiClient.get(`/materials/${id}`),
    create: (data) => apiClient.post('/materials', data),
    update: (id, data) => apiClient.put(`/materials/${id}`, data),
    delete: (id) => apiClient.delete(`/materials/${id}`)
  },
  
  // Shop/E-commerce endpoints
  shop: {
    firstSip: {
      claim: data => apiClient.post('/v1/shop/first-sip/claim', data),
      challenge: data => apiClient.post('/v1/shop/first-sip/challenge', data),
      verify: data => apiClient.post('/v1/shop/first-sip/verify', data)
    },
    // Products
    products: {
      getAll: () => catalogClient.get('/v1/shop/products'),
      getById: (id) => catalogClient.get(`/v1/shop/products/${id}`),
      getBySku: (sku) => apiClient.get(`/v1/shop/products/sku/${sku}`),
      checkAvailability: (id, quantity) =>
        apiClient.get(`/v1/shop/products/${id}/availability`, { params: { quantity } })
    },
    
    // Cart
    cart: {
      create: () => apiClient.post('/v1/shop/cart'),
      get: (sessionId) => apiClient.get(`/v1/shop/cart/${sessionId}`),
      addItem: (sessionId, productId, quantity) => 
        apiClient.post(`/v1/shop/cart/${sessionId}/items`, { productId, quantity }),
      updateItem: (sessionId, productId, quantity) => 
        apiClient.put(`/v1/shop/cart/${sessionId}/items/${productId}`, { quantity }),
      removeItem: (sessionId, productId) => 
        apiClient.delete(`/v1/shop/cart/${sessionId}/items/${productId}`),
      clear: (sessionId) => apiClient.delete(`/v1/shop/cart/${sessionId}`),
      merge: (sessionId) => apiClient.post('/v1/shop/cart/merge', { sessionId })
    },
    
    // Checkout
    checkout: {
      paymentConfig: () => apiClient.get('/v1/shop/checkout/payment-config'),
      guest: (data) => apiClient.post('/v1/shop/checkout/guest', data),
      // Settles an order held for 3-D Secure; the server re-reads the intent
      // from Stripe rather than trusting the browser's word that it passed.
      confirm: (orderNumber) => apiClient.post('/v1/shop/checkout/confirm', { orderNumber }),
      verifyAge: (data) => apiClient.post('/v1/shop/checkout/age-verify', data),
      calculateShipping: (data) => apiClient.post('/v1/shop/checkout/shipping-rates', data)
    },
    
    // Orders
    orders: {
      getByNumber: (orderNumber) => apiClient.get(`/v1/shop/orders/${orderNumber}`),
      getConfirmation: (orderNumber) => apiClient.get(`/v1/shop/orders/${orderNumber}/confirmation`)
    },
    
    // Authentication
    auth: {
      login: (credentials) => apiClient.post('/v1/shop/auth/login', credentials),
      register: (userData) => apiClient.post('/v1/shop/auth/register', userData),
      logout: () => apiClient.post('/v1/shop/auth/logout'),
      verify: (token) => apiClient.get('/v1/shop/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      })
    }
  },
  
  // Direct access to axios methods for backward compatibility
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  delete: (url, config) => apiClient.delete(url, config)
}
